/* eslint-disable max-len */
import {
  Gender,
  IbsClassification,
  MSKPainInterferenceLevel,
} from '../../businessLogic/AssessmentQuestionTypes';
import { animations } from '../../components/assets/Animations';
import * as images from '../../components/assets/Images';
import {
  document as documentIcon,
  filters,
  heartbeat,
  shakingHands,
  target,
} from '../../components/assets/images/Icons';
import { routeBy } from '../../components/flows/FlowNavigation';
import { AuthLayoutConfig } from '../../components/flows/layouts/AuthLayout';
import { getAuthMethodUsed, getPaymentRoute } from '../../util/ConditionalNextScreenSelectors';
import {
  getCoverageEntryRoute,
  getCoverageResultsRoute,
  getPartnerInsurerAccessRoute,
} from '../../util/CoverageSelectors';
import { InsuranceSelectors } from '../../util/InsuranceSelectors';
import { US_65_PLUS_ROUTE, getUs65PlusRoute } from '../../util/MedicareAccess';
import {
  CoverageScreenName,
  FlowConfig,
  GAD7ScreenName,
  MedicareAccessScreenName,
  PHQ9ScreenName,
  PapScreenName,
  ScreenConfig,
  ScreenType,
  SharedScreenName,
  Us65PlusScreenName,
} from '../FlowTypes';
import { HEALTH_COVER_MEDICARE_ORIGINAL } from '../InsuranceProviders';
import {
  AU_COVERAGE_CHECK_LAYOUT,
  COVERAGE_CHECK_LAYOUT,
  COVERAGE_RESULTS_LAYOUT,
  COVERAGE_SCREENS,
  PARTNER_INSURER_ACCESS_LAYOUT,
} from './CoverageConfig';
import {
  HEALTH_COVER_LAYOUT,
  MEDICARE_ACCESS_SCREENS,
  MEDICARE_ACCESS_STATE_LAYOUT,
} from './MedicareAccessConfig';
import { NervaControlScreenName, NEXT_BUTTON_TITLE } from './NervaControlScreenNames';
import { PATIENT_ASSISTANCE_SCREENS } from './PapConfig';

// Re-exported from the leaf module so existing `import { ... } from './NervaControl'` keep working.
export { NervaControlScreenName, NEXT_BUTTON_TITLE };

export const authLayout: AuthLayoutConfig = {
  registerTitle: 'One last step,\n so we can finalize your results!',
  registerBody: [
    'Enter your details to find out if addressing the gut-brain communication can help you manage your symptoms.',
  ],
  registerPrivacyDeclaration: [
    'Nerva will not sell any of your personal information. Ever. That’s a promise.',
  ],
  registerButtonTitle: 'See my results',
  loginTitle: 'Welcome back!',
  loginButtonTitle: 'See my program',
};

export const MENTAL_HEALTH_ISSUES_OPTIONS = ['Anxiety/Stress', 'Depression'];
export const IMPROVE_MENTAL_HEALTH_OPTIONS = [
  'Help improve other areas of my life (e.g. sleep, mental health)',
  'Help reduce my stress/anxiety',
];

export type NervaControlScreen =
  | SharedScreenName
  | NervaControlScreenName
  | PapScreenName
  | MedicareAccessScreenName
  | Us65PlusScreenName
  | GAD7ScreenName
  | PHQ9ScreenName
  | CoverageScreenName;

/**
 * A flow config consists of the following sections:
 * 1. INTRODUCTION: Collect user information and explain the product before authentication.
 * 2. ACCOUNT: A set of screens specifically for authentication (login, register, etc.)
 * 3. EDUCATION: A set of screens that explains the product further and reinforces the value proposition.
 * 4. PAYMENT: A set of screens that collects payment information.
 * 5. POST_PAYMENT: A set of screens that are shown after payment is complete. Including app download links, etc.
 */

export const INTRODUCTION_SCREENS: Array<ScreenConfig<NervaControlScreen>> = [
  {
    screenName: SharedScreenName.Gender,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Great, we will start by understanding a bit more about you and your symptoms.',
        'These responses will help us personalize your program.',
        'What’s your gender?',
      ],
      choices: [Gender.Male, Gender.Female, Gender.Nonbinary, Gender.Other, Gender.Unstated],
    },
    nextScreen: SharedScreenName.DateOfBirth,
  },

  {
    screenName: SharedScreenName.DateOfBirth,
    type: ScreenType.DateOfBirth,
    layout: {
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      infoBox: {
        titleText: 'Why do we ask this?',
        bodyText:
          'Knowing your date of birth helps us to understand your unique situation and helps your healthcare provider identify you.',
      },
    },
    nextScreen: SharedScreenName.Doctor,
  },

  {
    screenName: SharedScreenName.Doctor,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'A growing number of healthcare professionals are recommending Nerva.',
        'Were you recommended by a healthcare professional (a doctor, a nutritionist, etc.)?',
      ],
      choices: ['Yes', 'No', 'I’m a healthcare professional'],
    },
    nextScreen: {
      No: SharedScreenName.NoDoctorReferral,
      'I’m a healthcare professional': NervaControlScreenName.HCPGreeting,
      fallback: NervaControlScreenName.ReferralComparison,
    },
  },

  // START HCP SPECIFIC FLOW
  {
    screenName: NervaControlScreenName.HCPGreeting,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'It’s great to meet you! Nerva is a gut-directed hypnotherapy program for IBS patients that was created from a Monash research-backed protocol by Dr. Simone Peters.',
        },
        { type: 'image', src: images.simonePetersLarge },
        {
          type: 'text',
          content:
            'We’ve now partnered with over 5,000 healthcare professionals to better support over 150,000 of their patients.',
        },
        {
          type: 'text',
          content: 'Let’s discover if we can work with you to drive better patient outcomes.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.YourProfession,
  },

  {
    screenName: NervaControlScreenName.YourProfession,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['What is your profession?'],
      choices: [
        'Dietitian',
        'Primary Care / General Practitioner',
        'Gastroenterologist',
        'Naturopath',
        'Nutritionist',
        'Nurse',
        'Psychologist',
        'Physiotherapist',
        'Other',
      ],
    },
    nextScreen: NervaControlScreenName.HowManyIBSPatients,
  },

  {
    screenName: NervaControlScreenName.HowManyIBSPatients,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How many patients with IBS do you see a week?'],
      choices: ['0', '1-5', '6-10', '11-20', '20+'],
    },
    nextScreen: {
      '0': NervaControlScreenName.UnfortunateNoFit,
      fallback: NervaControlScreenName.ClassificationDGBI,
    },
  },

  {
    screenName: NervaControlScreenName.UnfortunateNoFit,
    type: ScreenType.ExternalLink,
    layout: {
      content: [
        {
          type: 'text',
          content: 'Unfortunately Nerva may not be a good fit for your practice.',
        },
        {
          type: 'text',
          content:
            'Nerva was built for IBS patients, with all existing research focused on patients with IBS.',
        },
        {
          type: 'text',
          content:
            'We do have have products in smoking cessation, menopause and mental health if you would like to explore.',
        },
      ],
      externalUrl: 'https://www.mindsethealth.com/clinician-overview',
    },
  },

  {
    screenName: NervaControlScreenName.ClassificationDGBI,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content: `As you would likely know, IBS is the most common GI disorder impacting 11% of of
					people in the US, and it was recently classified as a disorder of the brain-gut interaction.`,
        },
        { type: 'image', src: images.brainGutRealistic, fullWidth: true },
        {
          type: 'text',
          content: `IBS can be a debilitating condition with few effective management solutions, leading to feelings
					of frustration for patients and practitioners alike. We know from first-hand experience
					why it's so important to support people with IBS.`,
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.CurrentlyUtilise,
  },

  {
    screenName: NervaControlScreenName.CurrentlyUtilise,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Do you currently utilize any of these IBS management solutions with your patients?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Low-FODMAP',
        'Medications',
        'Peppermint',
        'Probiotics',
        'Supplements',
        'More/Other',
      ],
    },
    nextScreen: NervaControlScreenName.TraditionalOptions,
  },

  {
    screenName: NervaControlScreenName.TraditionalOptions,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content: `Most traditional approaches to managing IBS, such as dietary changes or medication,
					take a bottom-up approach and may overlook the crucial brain-gut connection.`,
        },
        { type: 'image', src: images.brainGutLine },
        {
          type: 'text',
          content: `With IBS being a disorder of the brain-gut interaction, including an approach that focuses on the brain-gut
					connection is necessary to drive the best outcomes.`,
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.FamiliarWithGDH,
  },

  {
    screenName: NervaControlScreenName.FamiliarWithGDH,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How familiar are you with gut-directed hypnotherapy?'],
      choices: ['Very', 'Somewhat', 'Not at all'],
    },
    nextScreen: NervaControlScreenName.EffectiveOfGDH,
  },

  {
    screenName: NervaControlScreenName.EffectiveOfGDH,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content: `Gut-directed hypnotherapy is a highly effective management option for IBS patients - with 70-80% of people
					seeing clinically significant improvements.`,
        },
        {
          type: 'text',
          content: `Dr. Peters conducted a study with Monash University which showed
					her 6-week protocol was as effective as the low FODMAP diet, without requiring diet change.`,
        },
        { type: 'image', src: images.hypnoFodmapChartWithMonashLogo },
        {
          type: 'text',
          content: `By addressing the miscommunication between the gut and the brain,
					patients can create a life less burdened by IBS.`,
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.AbilityToReferGDH,
  },

  {
    screenName: NervaControlScreenName.AbilityToReferGDH,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Even though gut-directed hypnotherapy is recommended by clinical practice guidelines (ACG, MJA, etc), it’s very inaccessible due to a lack of trained GI psychologists. Are you able to refer to gut-directed hypnotherapy currently?',
      ],
      choices: ['Yes', 'Sometimes', 'Not yet'],
    },
    nextScreen: NervaControlScreenName.FamiliarWithNerva,
  },

  {
    screenName: NervaControlScreenName.FamiliarWithNerva,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How familiar are you with Nerva?'],
      choices: ['Very', 'Somewhat', 'Not at all'],
    },
    nextScreen: NervaControlScreenName.NervaIntroduction,
  },

  {
    screenName: NervaControlScreenName.NervaIntroduction,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content: `Nerva is a self-guided 6-week gut-directed hypnotherapy program, available via a mobile app,
					designed to make evidence-based hypnotherapy accessible to everyone with IBS.`,
        },
        { type: 'image', src: images.nervaHolding },
        {
          type: 'text',
          content: `Developed by Dr. Simone Peters based on her research, Nerva has now helped over 150,000
					people with IBS supported by over 5,000 healthcare practitioners.`,
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.ReferredNerva,
  },

  {
    screenName: NervaControlScreenName.ReferredNerva,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['Have you referred Nerva before?'],
      choices: ['Yes', 'Not yet'],
    },
    nextScreen: NervaControlScreenName.DoYouSeeThesePatients,
  },

  {
    screenName: NervaControlScreenName.DoYouSeeThesePatients,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Do you see many of these IBS patients?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Resistant to dietary changes',
        'Stressed or anxious',
        'Risk of eating disorder',
        'Natural / holistic focused',
        'None',
      ],
    },
    nextScreen: NervaControlScreenName.PatientExamples,
  },

  {
    screenName: NervaControlScreenName.PatientExamples,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content: `These are good examples of patients who respond best when mentioning
					gut-directed hypnotherapy, but the research shows it is equally effective for all IBS patient types.`,
        },
        { type: 'image', src: images.examplePatients },
        {
          type: 'text',
          content: `As gut-directed hypnotherapy is non-invasive, it doesn't interact with other interventions,
					and it's low-risk with few side effects, Nerva is a great fit for every IBS patient.`,
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.ReferralConfidence,
  },

  {
    screenName: NervaControlScreenName.ReferralConfidence,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How confident do you feel recommending Nerva to your patients?'],
      choices: ['Very', 'Somewhat', 'Not confident yet'],
    },
    nextScreen: NervaControlScreenName.HCPCallToAction,
  },

  {
    screenName: NervaControlScreenName.HCPCallToAction,
    type: ScreenType.ExternalLink,
    layout: {
      content: [
        {
          type: 'text',
          content: 'Great! Thanks for sharing.',
        },
        {
          type: 'text',
          content: 'It seems Nerva would be a great fit for your IBS management protocols.',
        },
        {
          type: 'text',
          content:
            'It’s easy for your patients to begin Nerva today. Share patient details with Nerva in seconds via our secure web form. Patients referred by a practitioner automatically receive 25% off, with additional support available through our Patient Assistance Program.',
        },
      ],
      externalUrl: 'https://www.nervahealth.com/clinicians',
    },
  },

  // END HCP SPECIFIC FLOW

  {
    screenName: NervaControlScreenName.ReferralComparison,
    type: ScreenType.Presentation,

    layout: {
      footnotes: ['Internal data, Mindset Health, Program data 2024'],
      content: [
        { type: 'text', content: 'Thanks for letting us know!' },
        {
          type: 'text',
          content:
            'Over 11,000 clinicians actively refer Nerva to their patients, and these patients are 2x as likely to see improvement¹.',
        },
        {
          type: 'image',
          src: animations.referralChart,
          fullWidth: true,
          altText: 'referral comparison',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.HowLong,
  },

  {
    screenName: SharedScreenName.NoDoctorReferral,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'That’s okay! While you don’t need a referral to use Nerva, it’s important not to self diagnose. Many conditions can have similar symptoms.',
        },
        { type: 'image', src: images.doctor, altText: 'doctor' },
        {
          type: 'text',
          content: 'Let’s continue with your assessment and then create your personalized program.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.Attribution,
  },
  {
    screenName: SharedScreenName.Attribution,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['Where did you hear about us?'],
      choices: [
        'Facebook',
        'TikTok',
        'Reddit',
        'App Store',
        'Influencer',
        'Friends or family',
        'Instagram',
        'Search engine (Google, Bing)',
        'Blog post or news article',
        'Magazine or print',
        'Support group',
        'Other',
      ],
      randomizeChoices: true,
    },
    nextScreen: SharedScreenName.HowLong,
  },

  {
    screenName: SharedScreenName.HowLong,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How long have you had gastrointestinal symptoms and wanted to feel better?'],
      choices: ['0-6 months', '6-12 months', '1-5 years', '5-10 years', '10+ years'],
    },
    nextScreen: NervaControlScreenName.HowOften,
  },

  {
    screenName: NervaControlScreenName.HowOften,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How often do you experience gastrointestinal symptoms?'],
      choices: ['Multiple times a day', 'Daily', 'Multiple times a week', 'Weekly', 'Monthly'],
    },
    nextScreen: NervaControlScreenName.Symptoms,
  },

  {
    screenName: NervaControlScreenName.Symptoms,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['What physical symptoms do you normally experience during a flare-up?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Diarrhea',
        'Constipation',
        'Abdominal pain',
        'Bloating',
        { label: 'Gas/Wind', value: 'Wind' },
        'Nausea',
        'More/Other',
      ],
      toastMessage: 'Added to your program',
      toastDuration: 1000,
    },
    nextScreen: SharedScreenName.Classify,
  },

  {
    screenName: SharedScreenName.Classify,
    type: ScreenType.Classify,
    layout: {
      body: ['Does that mean you would classify yourself as having ', '?'],
      choices: [],
      toastMessage: 'Added to your program',
      toastDuration: 1000,
    },
    nextScreen: {
      [IbsClassification.IBS_C]: NervaControlScreenName.SocialProofClassifyIBSC,
      [IbsClassification.IBS_D]: NervaControlScreenName.SocialProofClassifyIBSD,
      [IbsClassification.IBS_M]: NervaControlScreenName.SocialProofClassifyIBSM,
      fallback: NervaControlScreenName.UpperGISymptoms,
    },
  },

  {
    screenName: NervaControlScreenName.SocialProofClassifyIBSM,
    type: ScreenType.SocialProof,
    layout: {
      body: [
        'You’re not alone. More than 18,047 people with IBS-M have learned how to better manage their gut symptoms by addressing their brain-gut connection.',
      ],
      reviewer: {
        name: 'Jason',
        testimonial: [
          '"I have struggled with IBS and anxiety-related stomach issues for years. This app has truly been one of the most impactful methods for helping me manage my issues. It’s incredible how much the brain and gut are connected, and this process really proves how successful psychology-based methods are for aiding in IBS relief."',
        ],
        date: 'United States, 2022',
      },
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.UpperGISymptoms,
  },

  {
    screenName: NervaControlScreenName.SocialProofClassifyIBSD,
    type: ScreenType.SocialProof,
    layout: {
      body: [
        'You’re not alone. More than 32,263 people with IBS-D have learned how to better manage their gut symptoms by addressing their brain-gut connection.',
      ],
      reviewer: {
        name: 'Margaret',
        testimonial: [
          '"I have suffered from IBS-D for more than 40 years. I’ve tried everything with marginal and very temporary results. I discovered Nerva IBS as part of my research. I became fed up with diet changes, medications and false promises. I got the first break from daily IBS events the first four days of trying the Nerva IBS program. I’ve been at it now for more than 3 months with only 3 IBS events total in that period of time. Best of all, these events were much less painful and I was able to remain calm throughout the process."',
        ],
        date: 'United States, 2024',
      },
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.UpperGISymptoms,
  },

  {
    screenName: NervaControlScreenName.SocialProofClassifyIBSC,
    type: ScreenType.SocialProof,
    layout: {
      body: [
        'You’re not alone. More than 25,615 people with IBS-C have learned how to better manage their gut symptoms by addressing their brain-gut connection.',
      ],
      reviewer: {
        name: 'Mandy',
        testimonial: [
          '"The Nerva program is a key part of my journey to recover from severe symptoms of IBS-C. The guided hypnotherapy sessions provide a consistent and supportive experience to manage reactions to stress, gain control of my gut in a relaxing and natural way. I highly recommend using Nerva."',
        ],
        date: 'United States, 2023',
      },
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.UpperGISymptoms,
  },

  {
    screenName: NervaControlScreenName.UpperGISymptoms,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Do you experience any other gastrointestinal symptoms?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Heartburn',
        'Dyspepsia',
        'Regurgitation',
        { label: 'Belching/Burping', value: 'Burping' },
        'Dysphagia',
        'More/Other',
        'None',
      ],
    },
    nextScreen: NervaControlScreenName.OtherGIIssues,
  },

  {
    screenName: NervaControlScreenName.OtherGIIssues,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Have you been diagnosed with any of the following gastrointestinal conditions?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Inflammatory Bowel Disease (IBD)',
        'Small Intestinal Bacterial Overgrowth (SIBO)',
        'Gastrointestinal Reflux Disease (GERD)',
        'Functional Dyspepsia',
        'Endometriosis',
        'Rumination Syndrome',
        'More/Other',
        'None',
      ],
    },
    nextScreen: {
      'Inflammatory Bowel Disease (IBD)': NervaControlScreenName.IBDSubtype,
      fallback: NervaControlScreenName.MSKChronicPain,
    },
  },

  {
    screenName: NervaControlScreenName.IBDSubtype,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['What IBD subtype have you been diagnosed with?'],
      choices: ['Crohn’s disease', 'Ulcerative colitis', 'I’m not sure'],
    },
    nextScreen: NervaControlScreenName.MSKChronicPain,
  },
  {
    screenName: NervaControlScreenName.MSKChronicPain,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Do you experience chronic pain in your back, neck, joints, muscles, or pelvic area that has lasted more than 3 months?',
      ],
      choices: ['Yes', 'No'],
    },
    nextScreen: {
      Yes: NervaControlScreenName.MSKPainInterference,
      No: NervaControlScreenName.Triggers,
      fallback: NervaControlScreenName.Triggers,
    },
  },

  {
    screenName: NervaControlScreenName.MSKPainInterference,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How much does this pain interfere with your daily activities?'],
      choices: [
        { label: 'Mild - I can mostly carry on with my day', value: MSKPainInterferenceLevel.Mild },
        {
          label: 'Moderate - It limits some of my daily activities',
          value: MSKPainInterferenceLevel.Moderate,
        },
        {
          label: 'Severe - It significantly impacts my ability to function',
          value: MSKPainInterferenceLevel.Severe,
        },
      ],
    },
    nextScreen: NervaControlScreenName.Triggers,
  },

  {
    screenName: NervaControlScreenName.Triggers,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['We know everyone’s triggers are unique. What are yours?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: ['Food', 'Anxiety/Stress', 'Medication', 'Exercise', 'Other', 'I’m not sure'],
      toastMessage: 'Added to your program',
      toastDuration: 1000,
    },
    nextScreen: NervaControlScreenName.EducationSignals,
  },

  {
    screenName: NervaControlScreenName.EducationSignals,
    type: ScreenType.Presentation,
    layout: {
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      footnotes: [
        'Coss-Adame, E. and Rao, S.S.C. (2014). Brain and Gut Interactions in Irritable Bowel Syndrome: New Paradigms and New Understandings. Current Gastroenterology Reports, [online] 16(4). doi:https://doi.org/10.1007/s11894-014-0379-z.',
      ],
      content: [
        {
          type: 'text',
          content:
            'Although people have different triggers, one of the key underlying reasons for the triggers is a miscommunication between the gut and the brain\u00B9.',
        },

        {
          type: 'image',
          src: animations.gutBrainConnection,
          altText: 'Diagram of the Brain-Gut Connection',
        },

        {
          type: 'text',
          content:
            'This miscommunication can lead to diarrhea, constipation and pain when reacting to common triggers like stress and food.',
        },
      ],
    },
    nextScreen: NervaControlScreenName.RelateButterflies,
  },

  {
    screenName: NervaControlScreenName.RelateButterflies,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Do you relate to the following statement?',
        '"I have experienced the feeling of having ’butterflies in my stomach’ when I have been nervous."',
      ],
      choices: ['Yes', 'Somewhat', 'No'],
    },
    nextScreen: NervaControlScreenName.RelateDropping,
  },

  {
    screenName: NervaControlScreenName.RelateDropping,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Do you relate to the following statement?',
        '"I have had a feeling of my stomach ’dropping’ in a moment of fear or receiving bad news."',
      ],
      choices: ['Yes', 'Somewhat', 'No'],
    },
    nextScreen: NervaControlScreenName.EducationExamples,
  },

  {
    screenName: NervaControlScreenName.EducationExamples,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'The ’dropping’ stomach or ’butterflies’ many people feel are examples of our gut and brain communicating with each other.',
        },
        {
          type: 'text',
          content: 'This communication can be impaired, leading  the gut to react unpredictably.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.RelateStress,
  },

  {
    screenName: NervaControlScreenName.RelateStress,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Do you relate to the following statement?',
        '"My symptoms worsen during periods of high stress or anxiety."',
      ],
      choices: ['Yes', 'Somewhat', 'No'],
    },
    nextScreen: NervaControlScreenName.RelateEmotions,
  },

  {
    screenName: NervaControlScreenName.RelateEmotions,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Do you relate to the following statement?',
        '"I’ve noticed a connection between my emotions and my symptoms."',
      ],
      choices: ['Yes', 'Somewhat', 'No'],
    },
    nextScreen: NervaControlScreenName.EducationStressCycle,
  },

  {
    screenName: NervaControlScreenName.EducationStressCycle,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'If you’ve ever stressed about experiencing symptoms, which triggers your symptoms, which then triggers your stress, then you’ve experienced the stress-symptom loop in action.',
        },
        {
          type: 'text',
          content:
            'Stress and anxiety is a clear example of the brain-gut connection stuck in an unhelpful loop that is hard to break without help.',
        },
        { type: 'image', src: animations.brainGutColour, fullWidth: true },
        {
          type: 'text',
          content:
            'You can break this cycle by fixing the miscommunication between your brain and gut, and helping you return to a calm & worry free state.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.RelateFood,
  },

  {
    screenName: NervaControlScreenName.RelateFood,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Do you relate to the following statement?',
        '"Certain foods seem to worsen my symptoms."',
      ],
      choices: ['Yes', 'Somewhat', 'No'],
    },
    nextScreen: NervaControlScreenName.EducationCertainFoods,
  },
  {
    screenName: NervaControlScreenName.EducationCertainFoods,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'This is a common experience for people with symptoms like yours. High FODMAP foods (like apples and onions)  rapidly ferment in the gut, causing the intestines to expand.',
        },
        {
          type: 'text',
          content: 'This expansion happens to everyone who digests high-FODMAP foods.',
        },
        {
          type: 'text',
          content:
            'However, those with have oversensitive nerves in their gastrointestinal tract (known as visceral hypersensitivity) can cause an overreaction to this expansion. These oversensitive nerves send false signals to the brain, resulting in painful and distressing symptoms.',
        },
        { type: 'image', src: animations.visceralHypnersensitivity },
        {
          type: 'text',
          content:
            'So you can either try to avoid eating foods which trigger these nerves, or change how the brain responds to the messages from the nerves via brain-gut focused therapy.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.ImpactLife,
  },

  {
    screenName: NervaControlScreenName.ImpactLife,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['How does dealing with these triggers and symptoms impact your life?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        {
          label: 'Physically uncomfortable',
          value: 'Feeling physically uncomfortable',
        },
        'Can’t eat foods I love',
        'Nervous to socialize',
        'Worrying about travel',
        'Struggle with work',
        'More/Other',
      ],
      toastMessage: 'Added to your program',
      toastDuration: 1000,
    },
    nextScreen: SharedScreenName.Treatments,
  },

  {
    screenName: SharedScreenName.Treatments,
    type: ScreenType.MultiSelect,
    layout: {
      body: [
        "Finding the right solution can be difficult; some people end up feeling like they've tried everything. What options have you tried?",
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Low FODMAP diet',
        'Neuromodulators',
        'Antispasmodics',
        'Probiotics',
        'Fibre supplements',
        'More/Other',
        'None',
      ],
    },
    nextScreen: {
      None: NervaControlScreenName.EducationUnderlyingReason,
      fallback: NervaControlScreenName.DidTreatmentsWork,
    },
  },

  {
    screenName: NervaControlScreenName.DidTreatmentsWork,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['Did they work?'],
      choices: ['Yes', 'Somewhat', 'No'],
    },
    nextScreen: NervaControlScreenName.EducationUnderlyingReason,
  },

  {
    screenName: NervaControlScreenName.EducationUnderlyingReason,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Most solutions only provide temporary or incomplete relief because they fail to target the root cause of the issue which is the miscommunication between the gut and brain.',
        },
        {
          type: 'text',
          content:
            'A recent study conducted by Dr. Simone Peters at Monash University found that targeting this miscommunication with gut-brain therapy provided effective long-term management without the use of diets or drugs.',
        },
        {
          type: 'image',
          src: images.educationUnderlyingReasonImage,
          marginBottom: false,
          layered: true,
        },
        {
          type: 'text',
          content:
            'Recent research such as this, has resulted in the classification of many conditions as a disorder of brain-gut interaction (DGBI) by leading clinical bodies such as the ROME foundation and British Society of Gastroenterology.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.Cotreatments,
  },

  {
    screenName: NervaControlScreenName.Cotreatments,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Are you currently engaging in or planning to begin any of the following treatments?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Low FODMAP diet',
        'Neuromodulators',
        'Antispasmodics',
        'Probiotics',
        'Fibre supplements',
        'More/Other',
        'None',
      ],
    },
    nextScreen: NervaControlScreenName.EducationCotreatments,
  },

  {
    screenName: NervaControlScreenName.EducationCotreatments,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Research shows people using Nerva see similar improvements regardless of other treatments they engage in¹.',
        },
        {
          type: 'text',
          content:
            "So you can feel confident that your Nerva experience won't be affected by other treatments.",
        },
      ],
      footnotes: [
        'Peters, S.L., Yao, C.K., Philpott, H., Yelland, G.W., Muir, J.G. and Gibson, P.R. (2016). Randomised clinical trial: the efficacy of gut-directed hypnotherapy is similar to that of the low FODMAP diet for the treatment of irritable bowel syndrome. Alimentary Pharmacology & Therapeutics, 44(5), pp.447–459. doi:10.1111/apt.13706.',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.IfFreeFromIbs,
  },

  {
    screenName: NervaControlScreenName.IfFreeFromIbs,
    type: ScreenType.MultiSelect,
    layout: {
      body: [
        'What would you want to do if you were free from your symptoms and regained control of your life?',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Eat the foods I love',
        {
          label: 'Enjoy social life/relationships',
          value: 'Enjoy my social life and relationships',
        },
        'Sleep better',
        'Travel with confidence',
        'Be more present at work',
        'More/Other',
      ],
      toastMessage: 'Added to your program',
      toastDuration: 1000,
    },
    nextScreen: NervaControlScreenName.LiveFreeNow,
  },

  {
    screenName: NervaControlScreenName.LiveFreeNow,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How important is achieving this to you?'],
      choices: ['Very important', 'Important', 'Not important'],
    },
    // Under-65 insurance is collected after sign-up (MaintenanceExperience → InsuranceProvider).
    ...routeBy(getUs65PlusRoute, {
      [US_65_PLUS_ROUTE]: MedicareAccessScreenName.MedicareAccessDirectHealthCover,
      fallback: SharedScreenName.Auth,
    }),
  },
  {
    // D2C Medicare-type ask before sign-up; getMedicareType reads it, so the Original branch can run
    // after Auth (MaintenanceExperience).
    screenName: MedicareAccessScreenName.MedicareAccessDirectHealthCover,
    type: ScreenType.SingleSelect,
    layout: HEALTH_COVER_LAYOUT,
    nextScreen: {
      [HEALTH_COVER_MEDICARE_ORIGINAL]: MedicareAccessScreenName.MedicareAccessState,
      fallback: SharedScreenName.Auth,
    },
  },
  {
    // Follows the 65+ Original Medicare answer. The ACCESS decision runs after Auth on
    // MaintenanceExperience, which reads this via isMedicareOriginal.
    screenName: MedicareAccessScreenName.MedicareAccessState,
    type: ScreenType.DropdownSelect,
    layout: MEDICARE_ACCESS_STATE_LAYOUT,
    nextScreen: SharedScreenName.Auth,
  },
];

export const ACCOUNT_SCREENS: Array<ScreenConfig<NervaControlScreen>> = [
  {
    screenName: SharedScreenName.Auth,
    type: ScreenType.Auth,
    layout: {
      ...authLayout,
    },
    ...routeBy(getAuthMethodUsed, {
      loginAndAlreadyPaid: SharedScreenName.AlreadyPaidDownloadApp,
      login: SharedScreenName.Results,
      register: NervaControlScreenName.AiLoading,
      fallback: SharedScreenName.Results,
    }),
  },

  {
    screenName: SharedScreenName.AlreadyPaidDownloadApp,
    type: ScreenType.DownloadApp,
    layout: {
      // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
      // Second line of Title coming from translation JSON to avoid 'missing translation' bug.
      title: ['You already have access!'],
      body: ['Use the same email and password you signed up with:'],
      alreadyPaid: true,
    },
  },
];

const EDUCATION_SCREENS: Array<ScreenConfig<NervaControlScreen>> = [
  {
    screenName: NervaControlScreenName.AiLoading,
    type: ScreenType.AiLoading,
    layout: {
      title: 'Loading Results...',
      subtitle: 'This takes just a moment',
      loadingBars: [
        {
          title: 'Analyzing your symptoms',
          icon: heartbeat,
          startNumber: 0,
          endNumber: 100,
          durationMs: 4000,
        },
        {
          title: 'Reviewing history',
          icon: documentIcon,
          startNumber: 0,
          endNumber: 100,
          durationMs: 5000,
        },
        {
          title: 'Assessing goals',
          icon: target,
          startNumber: 0,
          endNumber: 28,
          durationMs: 2000,
        },
        {
          title: 'Inferring preferences',
          icon: filters,
          startNumber: 0,
          endNumber: 17,
          durationMs: 1500,
        },
        {
          title: 'Establishing commitment',
          icon: shakingHands,
          isStatic: true,
          startNumber: 0,
          endNumber: 0,
          durationMs: 0,
        },
      ],
      footnoteHeader: 'Optimizing experience',
      footnote: 'Your results are ready...',
    },
    nextScreen: SharedScreenName.Results,
  },

  {
    screenName: SharedScreenName.Results,
    type: ScreenType.Results,
    layout: {
      title: 'The results are in',
      summaryBoxHeading: 'Nerva is likely to work\nfor you!',
      summaryBoxBody:
        '89% of people similar to you have better managed their symptoms by addressing their gut-brain connection.',
      body: [
        'Your survey results suggest that you are a great candidate for utilizing gut-brain therapy to better manage your symptoms.',
        'The next set of questions will help us get to know you better - your goals, preferences and commitment - so we can create a program that adapts to your unique needs and set your Results Date.',
        'Let’s continue to personalize your program.',
      ],
      primaryButtonTitle: 'Continue',
    },
    nextScreen: NervaControlScreenName.TimeCommitment,
  },

  {
    screenName: NervaControlScreenName.TimeCommitment,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How much time can you commit each day?'],
      choices: ['15 minutes', '15-30 minutes', '30-60 minutes', 'More than 1 hour'],
    },
    nextScreen: NervaControlScreenName.TimeOfDayNerva,
  },

  {
    screenName: NervaControlScreenName.TimeOfDayNerva,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['What’s the best time in your day to listen to Nerva?'],
      choices: [
        'When I first wake up',
        'In the morning',
        'At lunchtime',
        'In the afternoon/evening',
        'Before I fall asleep',
      ],
    },
    nextScreen: NervaControlScreenName.DaysPerWeekNerva,
  },

  {
    screenName: NervaControlScreenName.DaysPerWeekNerva,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'People who use the program more than 5 times per week see the best results.',
        'How many days can you listen to Nerva each week?',
      ],
      choices: ['3 days', '4 days', '5 days', '6 days', '7 days'],
    },
    nextScreen: NervaControlScreenName.BuildProgram,
  },

  {
    screenName: NervaControlScreenName.BuildProgram,
    type: ScreenType.SocialProof,
    layout: {
      body: [
        'Great! Listening to sessions regularly is the best way to see results.',
        'We’ll use this information to predict your Results Date and build a program that works for you.',
      ],
      reviewer: {
        name: 'Nerva story',
        testimonial: [
          '"After dealing with IBS symptoms for years and experiencing ups and downs, I found Nerva and I’m so happy that I took the chance! I made sure to stick to the plan as prescribed and noticed quickly a positive change in my symptoms.',
          'I completed the program and continue to use Nerva daily because I feel so much better and it’s a wonderful way to wind down at the end of the day.',
          'This is something all people diagnosed with IBS should have the opportunity to try. It works! Thank you, Nerva!"',
        ],
        date: '2024',
      },
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.DataSummary,
  },

  {
    screenName: NervaControlScreenName.DataSummary,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content: '89% of Nerva members improve their symptom management by their Results Date.',
        },
        { type: 'image', src: images.improvementStatsSocialProof, fullWidth: true },
        {
          type: 'text',
          content:
            'We’ll now create your personalized Nerva program and predict your Results Date.',
        },
        {
          type: 'text',
          content: 'Let’s finish personalizing your program.',
        },
      ],
      primaryButtonTitle: 'Create my program',
    },
    nextScreen: NervaControlScreenName.AiLoadingContinued,
  },

  {
    screenName: NervaControlScreenName.AiLoadingContinued,
    type: ScreenType.AiLoading,
    layout: {
      title: 'Personalizing your program',
      subtitle: 'This takes just a moment',
      loadingBars: [
        {
          title: 'Analyzing your symptoms',
          icon: heartbeat,
          startNumber: 100,
          endNumber: 100,
          durationMs: 0,
        },
        {
          title: 'Reviewing history',
          icon: documentIcon,
          startNumber: 100,
          endNumber: 100,
          durationMs: 0,
        },
        {
          title: 'Assessing goals',
          icon: target,
          startNumber: 28,
          endNumber: 100,
          durationMs: 3000,
        },
        {
          title: 'Inferring preferences',
          icon: filters,
          startNumber: 17,
          endNumber: 100,
          durationMs: 3500,
        },
        {
          title: 'Establishing commitment',
          icon: shakingHands,
          startNumber: 0,
          endNumber: 100,
          durationMs: 2500,
        },
      ],
      footnoteHeader: 'Optimizing experience',
      footnote: 'Ready! Finalizing setup...',
    },
    nextScreen: NervaControlScreenName.IbsResultsDate,
  },

  {
    screenName: NervaControlScreenName.IbsResultsDate,
    type: ScreenType.ProgramExperience,
    layout: {
      title: 'You can expect to start seeing results in 4-6 weeks',
      topBody: [
        'Based on your current personalized program, we predict that you can expect to see results and achieve your goals by:',
      ],
      showDate: true,
      bottomBody: [
        'This prediction is based on over 93,000 Nerva members who have completed their program with similar symptoms, triggers and goals.',
        'Click below to see the program we’ve built to help you reach your goals.',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.ProgramStructure,
  },

  {
    screenName: SharedScreenName.ProgramStructure,
    type: ScreenType.ProgramStructure,
    layout: {
      title: 'Manage your symptoms within 6 weeks',
      coreProgramWeekTitles: [
        'Trial the program to experience hypnotherapy',
        'Learn to feel more relaxed, with less anxiety and stress',
        'Calm your flare-ups with more confidence',
        'Build skills to address the stress-symptom cycle',
        'Start to see an improvement in your symptoms',
        'Return to normal gut functioning and maintain your symptom improvement',
      ],
      postProgramTitle: 'Beyond Week 6',
      postProgramContent: 'Maintain your results long-term',
      primaryButtonTitle: 'Get Started',
    },
    nextScreen: NervaControlScreenName.RepairGutBrainConnection,
  },

  {
    screenName: NervaControlScreenName.RepairGutBrainConnection,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Each session is like a lesson, slowly changing how your gut and brain interact. Over time, the communication becomes clearer - reducing the number of false alarms that cause your symptoms.',
        },
        { type: 'image', src: animations.brainGutCommunication, fullWidth: true },
        {
          type: 'text',
          content:
            'As you progress through the program, you’ll notice that your brain and body remain calm in scenarios that previously triggered flare-ups.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: NervaControlScreenName.BestSupported,
  },

  {
    screenName: NervaControlScreenName.BestSupported,
    type: ScreenType.MultiSelect,
    layout: {
      body: [
        'Nerva can help you better manage your symptoms in just 6 weeks, but it can also ensure you maintain these results long-term.',
        'How can we best support you after your Results Date?',
      ],
      choices: [
        'Help calm any flare-ups',
        'Help maintain my results',
        ...IMPROVE_MENTAL_HEALTH_OPTIONS,
        'Other',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.MaintenanceExperience,
  },

  {
    screenName: SharedScreenName.MaintenanceExperience,
    type: ScreenType.Presentation,
    layout: {
      title: 'Maintaining your Results',
      content: [
        {
          type: 'text',
          content:
            'In six weeks, after your Results Date, you will continue to have access to the program as you unlock customizable support tools. This includes new hypnotherapy sessions as well as your flare-up toolkit.',
        },
        { type: 'image', src: images.maintenanceProgramPreview, fullWidth: true },
        {
          type: 'text',
          content:
            'These tools are built into the program to ensure you sustain the results you have achieved for the long-term.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    // US 65+ Medicare Original → Medicare PROMs branch, then grant access via RevenueCat; US non-65+
    // → the coverage flow (payer screen first); everyone else → existing payment path.
    ...routeBy(InsuranceSelectors.getMaintenanceExperienceRoute, {
      medicareOriginal: MedicareAccessScreenName.MedicareAccessDisclosure,
      notCovered: MedicareAccessScreenName.MedicareAccessInsurerNotCovered,
      freeAccess: NervaControlScreenName.MoneyBackGuarantee,
      collectCoverage: NervaControlScreenName.InsuranceProvider,
      fallback: NervaControlScreenName.MoneyBackGuarantee,
    }),
  },
  {
    // No coupon here — the D2C 20% discount is applied at the payment page (US 65+ only).
    screenName: NervaControlScreenName.InsuranceProvider,
    type: ScreenType.InsuranceProvider,
    layout: {
      question: 'Which insurance provider do you have?',
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      applyInsuranceCoupon: false,
      coverageCheck: COVERAGE_CHECK_LAYOUT,
      auCoverageCheck: AU_COVERAGE_CHECK_LAYOUT,
    },
    // Only US non-65+ and AU users reach this screen (from MaintenanceExperience). A partner insurer
    // gets its no-cost access offer; everyone else continues into the coverage flow (US Original
    // Medicare skips straight to the address; AU goes to the membership number) unless they chose to
    // self-pay or have no private insurance (AU), which falls through to the payment path.
    ...routeBy(getCoverageEntryRoute, {
      partnerInsurer: CoverageScreenName.UsedPartnerInsurance,
      collectCoverage: CoverageScreenName.CoveragePlanType,
      medicareOriginal: CoverageScreenName.CoverageAddress,
      collectMemberId: CoverageScreenName.CoverageMemberId,
      fallback: NervaControlScreenName.MoneyBackGuarantee,
    }),
  },
  {
    // Original Medicare (dropdown or plan-type answer) enters the ACCESS pathway at the disclosure,
    // as MaintenanceExperience routed under-65 Original Medicare before; everyone else continues to
    // the payment path.
    screenName: CoverageScreenName.CoverageResults,
    type: ScreenType.CoverageResults,
    layout: COVERAGE_RESULTS_LAYOUT,
    assistanceScreen: PapScreenName.PapPublicAssistance,
    ...routeBy(getCoverageResultsRoute, {
      medicareOriginal: MedicareAccessScreenName.MedicareAccessDisclosure,
      fallback: NervaControlScreenName.MoneyBackGuarantee,
    }),
  },
  {
    screenName: CoverageScreenName.UsedPartnerInsurance,
    type: ScreenType.PartnerInsurerAccess,
    layout: PARTNER_INSURER_ACCESS_LAYOUT,
    ...routeBy(getPartnerInsurerAccessRoute, {
      partnerAccess: SharedScreenName.Payment,
      fallback: NervaControlScreenName.MoneyBackGuarantee,
    }),
  },
  {
    screenName: NervaControlScreenName.MoneyBackGuarantee,
    type: ScreenType.MoneyBackGuarantee,
    layout: {},
    nextScreen: SharedScreenName.Payment,
  },
];

export const PAYMENT_SCREENS: Array<ScreenConfig<NervaControlScreen>> = [
  {
    screenName: SharedScreenName.Payment,
    type: ScreenType.Payment,
    showHeaderBar: false,
    ...routeBy(getPaymentRoute, {
      newUser: SharedScreenName.FinishAccount,
      existingUser: SharedScreenName.DownloadApp,
      fallback: SharedScreenName.DownloadApp,
    }),
  },
];

export const POST_PAYMENT_SCREENS: Array<ScreenConfig<NervaControlScreen>> = [
  {
    screenName: SharedScreenName.FinishAccount,
    type: ScreenType.FinishAccount,
    layout: {
      // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
      // Title and info coming from translation JSON to avoid 'missing translation' bug.
      subtitle: 'Fill in these last few details and you’re ready to go!',
      primaryButtonTitle: 'Continue',
    },
    nextScreen: SharedScreenName.DownloadApp,
  },
  {
    screenName: SharedScreenName.DownloadApp,
    type: ScreenType.DownloadApp,
    layout: {
      title: ['You’re all set!', 'Access your program now'],
      // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
      // body coming from translation JSON to avoid ‘missing translation’ bug.
      alreadyPaid: false,
    },
  },
];

/**
 * Config for full Nerva flow.
 *
 * This should include all web-onboarding screens combined:
 * Introduction, Account, Education, Payment, Post-Payment screens
 */
export const NERVA_CONTROL_CONFIG: FlowConfig<NervaControlScreen> = {
  name: 'NervaControl',
  showProgressBar: true,
  // We fire an `o-flow-entered` event manually from the `Onboard` page (see `trackFlowEnteredAnalytics`) instead of
  // here because it needs to include traits derived from experiments being run.
  startEvent: undefined,
  screens: [
    ...INTRODUCTION_SCREENS,
    ...ACCOUNT_SCREENS,
    ...EDUCATION_SCREENS,
    ...PAYMENT_SCREENS,
    ...POST_PAYMENT_SCREENS,
    ...PATIENT_ASSISTANCE_SCREENS,
    ...MEDICARE_ACCESS_SCREENS,
    ...COVERAGE_SCREENS,
  ],
};
